import { ErrorWithBreadcrumb } from '~/errors';

export const useFitterHourStore = defineStore('FitterHour', () => {
  const hourRegistrationApi = useHourRegistrationAPI();
  const userConfigurationStore = useUserConfigurationStore();

  const fitterHourConfiguration = ref<FitterHourConfigurationResponse>();
  const fitterCategoriesDefault = ref<FitterCategoryDefault[]>();

  async function setFitterHourConfiguration(): Promise<void> {
    const response: ResponseDTO<FitterHourConfigurationResponse> | undefined = await hourRegistrationApi.getFitterHourConfiguration();

    if (response && response.HasErrors === false) {
      fitterHourConfiguration.value = response.Data;
    }
  }

  async function setFitterCategoriesDefault(fitterID: number): Promise<void> {
    const response: ResponseDTO<FitterCategoryDefault[]> | undefined = await hourRegistrationApi.getFitterCategoryDefaults(fitterID);

    if (response && response.HasErrors === false) {
      fitterCategoriesDefault.value = response.Data.filter((fitterCategory) => fitterCategory.IsAssignedToFitter);
    }
  }

  function $reset(): void {
    fitterHourConfiguration.value = {
      DefaultFitterCategoryUnit:
      {
        UnitsID: 0,
        UnitName: '',
        UnitShortName: ''
      },
      AppLightEnableProjectSearch: false,
      FitterEnableProjectSearch: false,
      FitterCategories: [],
      MinuteIncrementValue: null
    };
    fitterCategoriesDefault.value = [];
  }

  const config = computed(() => {
    if (fitterHourConfiguration.value === undefined) {
      throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.FitterHour, message: 'FitterHour configuration is not set.' });
    }

    return readonly(fitterHourConfiguration.value);
  });

  const appLightProjectsSearchIsEnabled = computed(() => config.value.AppLightEnableProjectSearch && userConfigurationStore.isLightUser);
  const fitterProjectSearchIsEnabled = computed(() => config.value.FitterEnableProjectSearch && userConfigurationStore.isLightUser === false);
  const searchProjectsIsEnabled = computed(() => appLightProjectsSearchIsEnabled.value || fitterProjectSearchIsEnabled.value || userConfigurationStore.isTeamLead);
  const minuteIncrement = computed(() => {
    if (config.value.MinuteIncrementValue === null) {
      throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.FitterHour, message: 'Minute increment value is not set in web.' });
    }

    return config.value.MinuteIncrementValue;
  });

  return {
    _fitterHourConfiguration: fitterHourConfiguration,
    fitterHourConfiguration: readonly(fitterHourConfiguration),
    setFitterHourConfiguration,
    _fitterCategoriesDefault: fitterCategoriesDefault,
    fitterCategoriesDefault: readonly(fitterCategoriesDefault),
    setFitterCategoriesDefault,
    $reset,
    searchAllProjectsIsEnabled: searchProjectsIsEnabled,
    minuteIncrement,
    config
  };
});
